import { z } from 'zod'
import { iso8601DateSchema, stringSchema } from './common'
import { storefrontViewSchema } from './storefront-view'

const venueSchema = z.object({
  name: stringSchema.nullish(),
  description: stringSchema.nullish(),
  address: stringSchema.nullish(),
  externalId: z.number().nullish(),
  timezone: z.string().nullish(),
  googleMapsLink: stringSchema.nullish(),
  stadiumImage: stringSchema.nullish(),
  seatWidgetId: stringSchema.nullish(),
  seatPlans: z.any(),
})

const extraSchema = z.object({
  margin: z.coerce.number().nullish(),
  exchangeRate: z.coerce.number().nullish(),
  maxReservationTicketCount: z.coerce.number().nullish(),
})

const competitionSchema = z.object({
  id: stringSchema.min(1),
  name: stringSchema.min(1),
})

const footballTeamSchema = z.object({
  id: stringSchema.min(1),
  name: stringSchema.min(1),
})

const faqSchema = z.object({
  position: z.number(),
  question: stringSchema.min(1),
  answer: stringSchema.min(1),
})

const variationSchema = z.object({
  externalId: stringSchema.min(1),
  name: stringSchema.min(1),
  description: stringSchema.nullish(),
})

const pathAlternativeSchema = z.object({
  path: stringSchema.min(1),
  siteId: stringSchema.min(1),
})

const soldOut = z.object({
  message: stringSchema.min(1).nullish(),
  modal: z.boolean(),
})

export const productSchema = z.object({
  id: stringSchema.min(1),
  parentId: stringSchema.nullish(),
  type: stringSchema.min(1),
  image: stringSchema.nullish(),
  media: z.array(z.any()).nullish(),
  path: stringSchema.min(1),
  pathAlternatives: z.array(pathAlternativeSchema),
  title: stringSchema.min(1),
  name: stringSchema.min(1),
  source: stringSchema.min(1),
  intro: stringSchema.nullish(),
  description: stringSchema.nullish(),
  ticketSupplierDescription: stringSchema.nullish(),
  body: stringSchema.nullish(),
  notes: stringSchema.nullish(),
  additionalDescription: stringSchema.nullish(),
  additionalNotes: stringSchema.nullish(),
  startPrice: z.number().nullish(),
  startDate: iso8601DateSchema.nullish(),
  startTime: stringSchema.nullish(),
  endDate: iso8601DateSchema.nullish(),
  endTime: stringSchema.nullish(),
  hasPerformances: z.boolean().nullish(),
  url: stringSchema.nullish(),
  venue: venueSchema,
  faqs: z.array(faqSchema).nullish(),
  competitions: z.array(competitionSchema).nullish(),
  footballTeams: z.array(footballTeamSchema).nullish(),
  keyFeatureLabels: z.any(),
  footballClubPages: z.any(),
  mandatoryHotel: z.boolean().nullish(),
  extra: extraSchema.nullish(),
  variations: z.array(variationSchema).nullish(),
  favourited: stringSchema.nullish(),
  soldOut: soldOut.nullish(),
  seoTitle: stringSchema.nullish(),
  seoDescription: stringSchema.nullish(),
  seoKeywords: stringSchema.nullish(),
  // Storefront views offered on this fixture (BOAT-1912); absent on list previews.
  views: z.array(storefrontViewSchema).default([]),
})

export const productPreviewSchema = productSchema
  .pick({
    id: true,
    type: true,
    image: true,
    path: true,
    title: true,
    startPrice: true,
    startDate: true,
    startTime: true,
    endDate: true,
    endTime: true,
    venue: true,
    favourited: true,
    keyFeatureLabels: true,
    competitions: true,
    footballTeams: true,
    url: true,
  })
  .extend({
    // Product-level price-drop flags (BOAT-1739/1740): the API only emits these
    // on deals-aware listings, so both are nullish. The "from" price still shows
    // the current price — the badge is what signals the drop.
    discounted: z.boolean().nullish(),
    bestDropPercent: z.number().nullish(),
  })
